/**
 * Configuração do assistente de ajuda.
 *
 * O provedor é qualquer um que fale o dialeto da OpenAI: a própria OpenAI, mas
 * também Groq, Together, OpenRouter, e um Ollama ou LM Studio rodando na sua
 * máquina. O que muda entre eles é o endereço.
 *
 * Os modelos NÃO se digitam. O botão pergunta ao provedor o que ele oferece e a
 * lista vem em caixas de seleção. Escrever o nome do modelo à mão é a forma mais
 * fácil de descobrir seis meses depois que ele foi aposentado e o chat está mudo
 * — e de errar uma letra sem que nada avise.
 *
 * A chave de API não aparece nesta tela e não entra no banco: ela mora em
 * `api/.env`, e o que se configura aqui é o NOME da variável que a contém.
 */

import { useCallback, useEffect, useState } from 'react';
import { api, messageOf } from '../../api/client.js';

interface Config {
  id: number;
  enabled: boolean;
  displayName: string;
  baseUrl: string;
  apiKeyEnv: string;
  enabledModels: string[];
  defaultModel: string | null;
  systemPrompt: string;
  temperature: number;
  maxTokens: number;
  historyTurns: number;
  ratePerHour: number;
  audience: 'todos' | 'autenticados' | 'admin';
  greeting: string | null;
}

interface Model {
  readonly id: string;
  readonly ownedBy: string | null;
  readonly chat: boolean;
}

/** Provedores conhecidos, para não obrigar ninguém a decorar endereço. */
const PRESETS: ReadonlyArray<{ label: string; url: string; hint: string }> = [
  { label: 'OpenAI', url: 'https://api.openai.com/v1', hint: 'gpt-4o, gpt-4o-mini, o3-mini' },
  { label: 'Groq', url: 'https://api.groq.com/openai/v1', hint: 'llama, mixtral — muito rápido' },
  { label: 'OpenRouter', url: 'https://openrouter.ai/api/v1', hint: 'catálogo de vários provedores' },
  { label: 'Together', url: 'https://api.together.xyz/v1', hint: 'modelos abertos hospedados' },
  { label: 'Ollama (local)', url: 'http://127.0.0.1:11434/v1', hint: 'na sua máquina, sem chave' },
  { label: 'LM Studio (local)', url: 'http://127.0.0.1:1234/v1', hint: 'na sua máquina, sem chave' },
];

export function ChatbotConfig(): JSX.Element {
  const [config, setConfig] = useState<Config | null>(null);
  const [keyPresent, setKeyPresent] = useState(false);
  // Provedor na própria máquina (Ollama, LM Studio) não pede chave; cobrar uma
  // dele seria travar o assistente por causa de um segredo que não existe.
  const [localProvider, setLocalProvider] = useState(false);
  const [defaultPrompt, setDefaultPrompt] = useState('');
  const [models, setModels] = useState<Model[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const data = await api.get<{
        config: Config; apiKeyPresent: boolean; localProvider: boolean; defaultPrompt: string;
      }>('/api/admin/chatbot');
      setConfig(data.config);
      setKeyPresent(data.apiKeyPresent);
      setLocalProvider(data.localProvider);
      setDefaultPrompt(data.defaultPrompt);
    } catch (e) {
      setError(messageOf(e, 'Não consegui ler a configuração.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = (change: Partial<Config>) => {
    setConfig((old) => (old ? { ...old, ...change } : old));
  };

  /** Pergunta ao provedor quais modelos ele tem, com o endereço da tela. */
  const discover = async () => {
    if (!config) return;

    setProbing(true);
    setError('');
    setNotice('');
    try {
      const data = await api.post<{
        models: Model[]; count: number; apiKeyPresent: boolean; localProvider: boolean;
      }>('/api/admin/chatbot/modelos', { baseUrl: config.baseUrl, apiKeyEnv: config.apiKeyEnv });
      setModels(data.models);
      setKeyPresent(data.apiKeyPresent);
      setLocalProvider(data.localProvider);
      setNotice(`${data.count} modelo(s) encontrados no provedor.`);
    } catch (e) {
      setError(messageOf(e, 'Não consegui falar com o provedor.'));
      setModels([]);
    } finally {
      setProbing(false);
    }
  };

  const save = async () => {
    if (!config) return;

    setSaving(true);
    setError('');
    setNotice('');
    try {
      await api.post('/api/admin/chatbot', config);
      setNotice('Configuração salva.');
      await load();
    } catch (e) {
      setError(messageOf(e, 'Não consegui salvar.'));
    } finally {
      setSaving(false);
    }
  };

  const toggleModel = (id: string) => {
    if (!config) return;

    const has = config.enabledModels.includes(id);
    const next = has
      ? config.enabledModels.filter((m) => m !== id)
      : [...config.enabledModels, id];

    patch({
      enabledModels: next,
      // Desmarcar o modelo padrão deixaria o chat apontando para algo que
      // ninguém liberou; ele passa a ser o primeiro que sobrou.
      defaultModel: has && config.defaultModel === id ? (next[0] ?? null) : (config.defaultModel ?? id),
    });
  };

  if (loading || !config) {
    return <div className="chatbot-shell"><p className="muted">carregando…</p></div>;
  }

  const visibleModels = showAll ? models : models.filter((m) => m.chat);
  const credentialsOk = keyPresent || localProvider;
  const ready = config.enabled && credentialsOk && config.defaultModel !== null;

  return (
    <div className="chatbot-shell">
      <header className="chatbot-head">
        <div>
          <h2>💬 Assistente de Ajuda</h2>
          <p className="muted">
            Um chat de suporte no canto da tela, ligado a qualquer provedor compatível com a
            API da OpenAI.
          </p>
        </div>
        <span className={`chatbot-state ${ready ? 'on' : 'off'}`}>
          {ready ? '● no ar' : '○ fora do ar'}
        </span>
      </header>

      {error && <p className="importer-error">⚠️ {error}</p>}
      {notice && <p className="importer-notice">✅ {notice}</p>}

      <section className="chatbot-card">
        <h3>1. O provedor</h3>

        <div className="chatbot-presets">
          {PRESETS.map((preset) => (
            <button
              key={preset.url}
              type="button"
              className={`chatbot-preset ${config.baseUrl === preset.url ? 'active' : ''}`}
              title={preset.hint}
              onClick={() => patch({ baseUrl: preset.url })}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <label className="form-field">
          <span>Endereço da API</span>
          <input
            type="text"
            className="form-input"
            value={config.baseUrl}
            placeholder="https://api.openai.com/v1"
            onChange={(e) => patch({ baseUrl: e.target.value })}
          />
          <small className="form-hint">Termina em <code>/v1</code> na maioria dos provedores.</small>
        </label>

        <label className="form-field">
          <span>Variável de ambiente com a chave</span>
          <input
            type="text"
            className="form-input"
            value={config.apiKeyEnv}
            onChange={(e) => patch({ apiKeyEnv: e.target.value })}
          />
          <small className={`form-hint ${credentialsOk ? 'good' : 'warn'}`}>
            {keyPresent
              ? `✅ ${config.apiKeyEnv} está definida em api/.env`
              : localProvider
                ? '✅ Provedor na própria máquina — não precisa de chave'
                : `⚠️ ${config.apiKeyEnv} não está definida em api/.env. Acrescente a linha `
                  + `${config.apiKeyEnv}=sua-chave e recarregue esta página.`}
          </small>
        </label>

        <button type="button" className="importer-secondary" disabled={probing} onClick={() => void discover()}>
          {probing ? 'perguntando ao provedor…' : '🔎 Buscar modelos disponíveis'}
        </button>
      </section>

      <section className="chatbot-card">
        <h3>2. Os modelos</h3>

        {models.length === 0 ? (
          <p className="muted">
            {config.enabledModels.length > 0 ? (
              <>
                Liberados agora: {config.enabledModels.join(', ')}. Use o botão acima para
                conferir se ainda existem no provedor.
              </>
            ) : (
              <>Busque os modelos acima para escolher quais o assistente pode usar.</>
            )}
          </p>
        ) : (
          <>
            <label className="toggle-label">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
              Mostrar também os que não são de conversa ({models.length - models.filter((m) => m.chat).length})
            </label>

            <div className="model-grid">
              {visibleModels.map((model) => {
                const on = config.enabledModels.includes(model.id);
                return (
                  <label key={model.id} className={`model-row ${on ? 'on' : ''}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleModel(model.id)} />
                    <span className="model-name">{model.id}</span>
                    {model.ownedBy && <span className="model-owner">{model.ownedBy}</span>}
                    {on && (
                      <button
                        type="button"
                        className={`model-default ${config.defaultModel === model.id ? 'active' : ''}`}
                        title="Usar este como padrão"
                        onClick={(e) => {
                          e.preventDefault();
                          patch({ defaultModel: model.id });
                        }}
                      >
                        {config.defaultModel === model.id ? '★ padrão' : '☆ tornar padrão'}
                      </button>
                    )}
                  </label>
                );
              })}
            </div>
          </>
        )}
      </section>

      <section className="chatbot-card">
        <h3>3. O comportamento</h3>

        <div className="form-row-2">
          <label className="form-field">
            <span>Nome do assistente</span>
            <input
              type="text"
              className="form-input"
              value={config.displayName}
              onChange={(e) => patch({ displayName: e.target.value })}
            />
          </label>
          <label className="form-field">
            <span>Quem pode usar</span>
            <select
              className="form-select"
              value={config.audience}
              onChange={(e) => patch({ audience: e.target.value as Config['audience'] })}
            >
              <option value="autenticados">Quem está logado</option>
              <option value="todos">Qualquer visitante</option>
              <option value="admin">Só administradores</option>
            </select>
          </label>
        </div>

        <label className="form-field">
          <span>Saudação (a primeira frase, antes de qualquer pergunta)</span>
          <input
            type="text"
            className="form-input"
            maxLength={500}
            value={config.greeting ?? ''}
            placeholder="Oi! Pergunte sobre as regras, o editor de mapas ou a campanha."
            onChange={(e) => patch({ greeting: e.target.value })}
          />
        </label>

        <label className="form-field">
          <span>Instrução de sistema — quem o assistente é e o que ele faz</span>
          <textarea
            className="form-input chatbot-prompt"
            rows={10}
            value={config.systemPrompt}
            onChange={(e) => patch({ systemPrompt: e.target.value })}
          />
          <button
            type="button"
            className="linklike"
            onClick={() => patch({ systemPrompt: defaultPrompt })}
          >
            restaurar o texto padrão
          </button>
        </label>

        <div className="form-row-2">
          <label className="form-field">
            <span>Temperatura ({config.temperature.toFixed(2)})</span>
            <input
              type="range"
              min={0}
              max={2}
              step={0.05}
              value={config.temperature}
              onChange={(e) => patch({ temperature: Number(e.target.value) })}
            />
            <small className="form-hint">
              Baixa responde igual e previsível; alta varia mais. Para suporte, entre 0,2 e 0,5.
            </small>
          </label>
          <label className="form-field">
            <span>Tamanho máximo da resposta</span>
            <input
              type="number"
              className="form-input"
              min={64}
              max={8000}
              value={config.maxTokens}
              onChange={(e) => patch({ maxTokens: Number(e.target.value) })}
            />
          </label>
        </div>

        <div className="form-row-2">
          <label className="form-field">
            <span>Mensagens de histórico por pergunta</span>
            <input
              type="number"
              className="form-input"
              min={0}
              max={40}
              value={config.historyTurns}
              onChange={(e) => patch({ historyTurns: Number(e.target.value) })}
            />
            <small className="form-hint">Quanto da conversa vai junto. Mais contexto custa mais.</small>
          </label>
          <label className="form-field">
            <span>Limite de perguntas por hora, por pessoa</span>
            <input
              type="number"
              className="form-input"
              min={0}
              max={500}
              value={config.ratePerHour}
              onChange={(e) => patch({ ratePerHour: Number(e.target.value) })}
            />
            <small className="form-hint">
              Zero desliga o limite. Cada pergunta custa na conta do provedor.
            </small>
          </label>
        </div>
      </section>

      <div className="chatbot-actions">
        <label className="toggle-label big">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => patch({ enabled: e.target.checked })}
          />
          Assistente ligado
        </label>
        <button type="button" className="importer-submit" disabled={saving} onClick={() => void save()}>
          {saving ? 'salvando…' : '💾 Salvar configuração'}
        </button>
      </div>

      {config.enabled && !credentialsOk && (
        <p className="importer-error">
          ⚠️ O assistente está marcado como ligado, mas a chave não foi encontrada — ele não vai
          aparecer para ninguém até <code>{config.apiKeyEnv}</code> existir em <code>api/.env</code>.
        </p>
      )}
      {config.enabled && credentialsOk && config.defaultModel === null && (
        <p className="importer-error">
          ⚠️ Falta escolher o modelo padrão. Busque os modelos e marque um como ★ padrão.
        </p>
      )}
    </div>
  );
}
